/*
 * /auth/bootstrap — one-time first-admin setup
 *
 * Accessible to any logged-in user. The mutation refuses to run if any
 * admin already exists, so this page is safe to leave deployed.
 */
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Page,
} from "@emr/react-ui"
import { supabase } from "@emr/auth/client"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ShieldCheck } from "lucide-react"
import { useState } from "react"
import { trpc } from "../../trpc"
import { authPageLoaderSoft } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/bootstrap")({
	component: BootstrapPage,
	// Allow any logged-in user — unapproved users included
	loader: authPageLoaderSoft({ allowUnauthorized: true }),
})

function BootstrapPage() {
	const loaderData = Route.useLoaderData()
	const navigate = useNavigate()
	const [error, setError] = useState<string | null>(null)
	const [done, setDone] = useState(false)

	const bootstrap = trpc.user.bootstrap.useMutation({
		onSuccess: async () => {
			setDone(true)
			// Refresh the JWT so the new role is reflected immediately
			await supabase.auth.refreshSession()
			navigate({ to: "/auth/admin" })
		},
		onError: (e) => setError(e.message),
	})

	const email = (loaderData as any)?.user?.email ?? "your account"

	return (
		<Page>
			<Container width="sm" padding="lg">
				<Card>
					<CardHeader>
						<div className="flex items-center gap-3">
							<ShieldCheck className="w-6 h-6 text-primary" />
							<CardTitle>First-Time Admin Setup</CardTitle>
						</div>
					</CardHeader>
					<CardContent className="flex flex-col gap-4">
						<p className="text-sm text-muted-foreground">
							This page promotes the currently signed-in account to{" "}
							<strong>Super Admin</strong>. It only works when no admin exists yet
							— once an admin is present, this button does nothing.
						</p>

						<div className="rounded-md border bg-muted/40 px-4 py-3 text-sm">
							Signed in as <strong>{email}</strong>
						</div>

						{error && (
							<p className="text-sm text-destructive">{error}</p>
						)}

						{done ? (
							<p className="text-sm text-green-600 font-medium">
								Done — redirecting to admin…
							</p>
						) : (
							<Button
								onClick={() => {
									setError(null)
									bootstrap.mutate()
								}}
								disabled={bootstrap.isPending}
								className="w-full"
							>
								{bootstrap.isPending ? "Claiming…" : "Claim Admin Access"}
							</Button>
						)}
					</CardContent>
				</Card>
			</Container>
		</Page>
	)
}
